import type {
	AppConfigMasteryLevelsType,
	SessionConfigFiltersType,
} from "@memorang/constants/config/schema";
import type { Distribution, Item, MixedItemTag } from "@memorang/types";
import type { SectionTag } from "@memorang/ui/components/FilterSections/types";
import type { CheckboxState } from "../types/filter";

export const TAG_TYPES = {
	skill: "skill",
	questionType: "Question Type",
	proficiency: "Proficiency",
};

const SKILL_PRIORITY = ["Reading", "Listening"];

const MASTERY_PRIORITY_ORDER: (keyof Distribution)[] = [
	"low",
	"none",
	"medium",
	"high",
];
const masteryPriorityRank = new Map(
	MASTERY_PRIORITY_ORDER.map((key, index) => [key, index]),
);

export const sortByMasteryPriority = (
	keys: (keyof Distribution)[],
): (keyof Distribution)[] =>
	[...keys].sort(
		(a, b) =>
			(masteryPriorityRank.get(a) ?? 999) - (masteryPriorityRank.get(b) ?? 999),
	);

const tagsOfType = (tags: MixedItemTag[], type: string) =>
	tags.filter((tag) => tag.type === type);

const toItemIdSet = (tag: MixedItemTag) =>
	new Set((tag.items ?? []).map((item) => item.id));

const findCheckedSkillTag = (
	topLevelTags: MixedItemTag[],
	status: CheckboxState,
) =>
	topLevelTags.find((tag) => tag.type === TAG_TYPES.skill && status?.[tag.id]);

const collectCheckedItemIds = (
	topLevelTags: MixedItemTag[],
	type: string,
	status: CheckboxState,
	emptyFacetMeansNoFilter = true,
): Set<Item["id"]> | undefined => {
	const tags = tagsOfType(topLevelTags, type);
	if (!tags.length) {
		// No tags of this type exist at all — always "no filter", regardless of
		// emptyFacetMeansNoFilter (which only governs tags that exist but are
		// unchecked).
		return undefined;
	}
	const checkedTags = tags.filter((tag) => status?.[tag.id]);
	if (!checkedTags.length) {
		return emptyFacetMeansNoFilter ? undefined : new Set();
	}
	return new Set(
		checkedTags.flatMap((tag) => tag.items ?? []).map((item) => item.id),
	);
};

const buildEmptyDistribution = (
	distributionFilterItems: SessionConfigFiltersType,
): Distribution =>
	Object.assign(
		distributionFilterItems.reduce<Distribution>((acc, level) => {
			acc[level.distKey] = 0;
			return acc;
		}, {}),
		{ none: 0 },
	);

export const getQuestionTypesForSkill = (
	questionTypeTags: MixedItemTag[],
	skillTag: MixedItemTag | undefined,
): MixedItemTag[] => {
	if (!skillTag) {
		return questionTypeTags;
	}
	const skillItemIds = toItemIdSet(skillTag);
	return questionTypeTags.filter((qt) =>
		(qt.items ?? []).some((item) => skillItemIds.has(item.id)),
	);
};

const skillHasFreeQuestionType = (
	skillTag: MixedItemTag,
	questionTypeTags: MixedItemTag[],
): boolean => {
	if (!questionTypeTags.length) {
		return true;
	}
	return getQuestionTypesForSkill(questionTypeTags, skillTag).some(
		(qt) => !qt.locked,
	);
};

export const getDefaultSkillId = (
	skillTags: MixedItemTag[],
	questionTypeTags: MixedItemTag[] = [],
): number | undefined => {
	if (!skillTags.length) {
		return undefined;
	}

	const reachable = skillTags.filter(
		(tag) => !tag.locked && skillHasFreeQuestionType(tag, questionTypeTags),
	);
	const candidates = reachable.length ? reachable : skillTags;

	for (const name of SKILL_PRIORITY) {
		const tag = candidates.find((t) => t.title === name);
		if (tag && (tag.averageScore ?? 0) < 100) {
			return tag.id;
		}
	}
	return (
		candidates.find((t) => (t.averageScore ?? 0) < 100)?.id ?? candidates[0]?.id
	);
};

export const getDefaultProficiencyIds = (
	proficiencyTags: MixedItemTag[],
	proficiencyLevels: AppConfigMasteryLevelsType | undefined,
): number[] => {
	const reachableTags = proficiencyTags.filter((tag) => !tag.locked);
	const availableTags = reachableTags.length ? reachableTags : proficiencyTags;
	if (!availableTags.length) {
		return [];
	}

	const ordered = proficiencyLevels?.length
		? [...availableTags].sort((a, b) => {
				const ai = proficiencyLevels.findIndex(
					(p) => p.alias.title === a.title,
				);
				const bi = proficiencyLevels.findIndex(
					(p) => p.alias.title === b.title,
				);
				return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
			})
		: availableTags;

	const underThreshold = ordered.filter((t) => (t.averageScore ?? 0) < 90);
	return underThreshold.length
		? underThreshold.map((t) => t.id)
		: ordered.map((t) => t.id);
};

/**
 * Quick Study Custom-tab selection: the requested skill (or the default one)
 * plus exactly the requested question types / proficiencies that apply and
 * have items. Nothing else is pre-checked — an empty facet means "all".
 */
export const buildQuickStudyTagSelection = (
	topLevelTags: MixedItemTag[],
	selectedTagIds: number[] | undefined,
): Record<string | number, boolean> => {
	const selectedSet = new Set(selectedTagIds ?? []);
	const skillTags = tagsOfType(topLevelTags, TAG_TYPES.skill);
	const questionTypeTags = tagsOfType(topLevelTags, TAG_TYPES.questionType);

	const selectedSkillTag =
		skillTags.find((t) => selectedSet.has(t.id)) ??
		skillTags.find(
			(t) => t.id === getDefaultSkillId(skillTags, questionTypeTags),
		);
	const applicableQuestionTypeIds = new Set(
		getQuestionTypesForSkill(questionTypeTags, selectedSkillTag).map(
			(t) => t.id,
		),
	);
	const hasItems = (tag: MixedItemTag) => (tag.numItems ?? 0) > 0;

	const status: Record<string | number, boolean> = {};
	for (const tag of topLevelTags) {
		if (tag.type === TAG_TYPES.skill) {
			status[tag.id] = tag.id === selectedSkillTag?.id;
		} else if (tag.type === TAG_TYPES.questionType) {
			status[tag.id] =
				selectedSet.has(tag.id) &&
				applicableQuestionTypeIds.has(tag.id) &&
				hasItems(tag);
		} else if (tag.type === TAG_TYPES.proficiency) {
			status[tag.id] = selectedSet.has(tag.id) && hasItems(tag);
		} else {
			status[tag.id] = selectedSet.has(tag.id);
		}
	}
	return status;
};

export const buildStructuredTagSelection = (
	topLevelTags: MixedItemTag[],
	selectedTagIds: number[],
	proficiencyLevels: AppConfigMasteryLevelsType | undefined,
): Record<string | number, boolean> => {
	const selectedSet = new Set(selectedTagIds);
	const skillTags = tagsOfType(topLevelTags, TAG_TYPES.skill);
	const questionTypeTags = tagsOfType(topLevelTags, TAG_TYPES.questionType);
	const proficiencyTags = tagsOfType(topLevelTags, TAG_TYPES.proficiency);

	const selectedSkillTag =
		skillTags.find((t) => selectedSet.has(t.id)) ??
		skillTags.find(
			(t) => t.id === getDefaultSkillId(skillTags, questionTypeTags),
		);

	const applicableQuestionTypes = getQuestionTypesForSkill(
		questionTypeTags,
		selectedSkillTag,
	);
	const selectedQuestionTypes = applicableQuestionTypes.filter((t) =>
		selectedSet.has(t.id),
	);
	const checkedQuestionTypeIds = new Set(
		(selectedQuestionTypes.length
			? selectedQuestionTypes
			: applicableQuestionTypes
		).map((t) => t.id),
	);

	const selectedProficiencies = proficiencyTags.filter((t) =>
		selectedSet.has(t.id),
	);
	const checkedProficiencyIds = new Set(
		selectedProficiencies.length
			? selectedProficiencies.map((t) => t.id)
			: getDefaultProficiencyIds(proficiencyTags, proficiencyLevels),
	);

	const status: Record<string | number, boolean> = {};
	for (const tag of topLevelTags) {
		if (tag.type === TAG_TYPES.skill) {
			status[tag.id] = tag.id === selectedSkillTag?.id;
		} else if (tag.type === TAG_TYPES.questionType) {
			status[tag.id] = checkedQuestionTypeIds.has(tag.id);
		} else if (tag.type === TAG_TYPES.proficiency) {
			status[tag.id] = checkedProficiencyIds.has(tag.id);
		} else {
			status[tag.id] = selectedSet.has(tag.id);
		}
	}
	return status;
};

export const applySkillSelection = (
	topLevelTags: MixedItemTag[],
	currentStatus: CheckboxState,
	skillTag: MixedItemTag,
	rememberedQuestionTypeSelection?: CheckboxState,
): CheckboxState => {
	const status: CheckboxState = { ...currentStatus };
	for (const tag of tagsOfType(topLevelTags, TAG_TYPES.skill)) {
		status[tag.id] = tag.id === skillTag.id;
	}
	const questionTypeTags = tagsOfType(topLevelTags, TAG_TYPES.questionType);
	const applicableIds = new Set(
		getQuestionTypesForSkill(questionTypeTags, skillTag).map((t) => t.id),
	);
	for (const tag of questionTypeTags) {
		status[tag.id] = applicableIds.has(tag.id)
			? (rememberedQuestionTypeSelection?.[tag.id] ?? false)
			: false;
	}
	return status;
};

export const extractQuestionTypeSelection = (
	topLevelTags: MixedItemTag[],
	status: CheckboxState,
): CheckboxState => {
	const selection: CheckboxState = {};
	for (const tag of tagsOfType(topLevelTags, TAG_TYPES.questionType)) {
		selection[tag.id] = !!status?.[tag.id];
	}
	return selection;
};

export const getUnavailableQuestionTypeIds = (
	topLevelTags: MixedItemTag[],
	selectedSkillTag: MixedItemTag | undefined,
): Set<number> => {
	if (!selectedSkillTag) {
		return new Set();
	}
	const questionTypeTags = tagsOfType(topLevelTags, TAG_TYPES.questionType);
	const applicableIds = new Set(
		getQuestionTypesForSkill(questionTypeTags, selectedSkillTag).map(
			(t) => t.id,
		),
	);
	return new Set(
		questionTypeTags.filter((t) => !applicableIds.has(t.id)).map((t) => t.id),
	);
};

export const computeStructuredTagsDist = (
	topLevelTags: MixedItemTag[],
	status: CheckboxState,
	distributionFilterItems: SessionConfigFiltersType,
	emptyFacetMeansNoFilter = true,
): Distribution => {
	const skillTag = findCheckedSkillTag(topLevelTags, status);
	const questionTypeItemIds = collectCheckedItemIds(
		topLevelTags,
		TAG_TYPES.questionType,
		status,
		emptyFacetMeansNoFilter,
	);
	const proficiencyItemIds = collectCheckedItemIds(
		topLevelTags,
		TAG_TYPES.proficiency,
		status,
		emptyFacetMeansNoFilter,
	);

	const dist = buildEmptyDistribution(distributionFilterItems);

	const items = (skillTag?.items ?? []).filter(
		(item) =>
			(!questionTypeItemIds || questionTypeItemIds.has(item.id)) &&
			(!proficiencyItemIds || proficiencyItemIds.has(item.id)),
	);

	for (const item of items) {
		dist[item.distribution] = (dist[item.distribution] ?? 0) + 1;
	}

	return dist;
};

export const computeStructuredTagCounts = (
	topLevelTags: MixedItemTag[],
	status: CheckboxState,
	distributionStatus: CheckboxState,
): Record<number, number> => {
	const skillTag = findCheckedSkillTag(topLevelTags, status);
	if (!skillTag) {
		return {};
	}
	const skillItems = skillTag.items ?? [];

	const checkedQuestionTypeItemIds = collectCheckedItemIds(
		topLevelTags,
		TAG_TYPES.questionType,
		status,
	);
	const checkedProficiencyItemIds = collectCheckedItemIds(
		topLevelTags,
		TAG_TYPES.proficiency,
		status,
	);

	const checkedMasteryKeys = Object.keys(distributionStatus ?? {}).filter(
		(key) => distributionStatus?.[key],
	);
	const masterySet = checkedMasteryKeys.length
		? new Set(checkedMasteryKeys)
		: undefined;

	const countForTag = (
		tag: MixedItemTag,
		otherFacetItemIds: Set<Item["id"]> | undefined,
	) => {
		const tagItemIds = toItemIdSet(tag);
		return skillItems.filter(
			(item) =>
				tagItemIds.has(item.id) &&
				(!otherFacetItemIds || otherFacetItemIds.has(item.id)) &&
				(!masterySet || masterySet.has(item.distribution)),
		).length;
	};

	const counts: Record<number, number> = {};
	for (const tag of topLevelTags) {
		if (tag.type === TAG_TYPES.questionType) {
			counts[tag.id] = countForTag(tag, checkedProficiencyItemIds);
		} else if (tag.type === TAG_TYPES.proficiency) {
			counts[tag.id] = countForTag(tag, checkedQuestionTypeItemIds);
		}
	}
	return counts;
};

const isFacetTag = (tag: MixedItemTag) =>
	tag.type === TAG_TYPES.questionType || tag.type === TAG_TYPES.proficiency;

/**
 * Unchecks question-type / proficiency tags that are checked but currently
 * have zero items given the other facets, so state matches the rows the user
 * sees. Removing a stale tag never lowers another checked tag's count, so one
 * pass reaches the fixed point; the loop is a cheap guard.
 */
export const pruneUnavailableSelections = (
	topLevelTags: MixedItemTag[],
	status: CheckboxState,
	distributionStatus: CheckboxState,
): CheckboxState => {
	if (!status) {
		return status;
	}
	let next: NonNullable<CheckboxState> = status;
	for (;;) {
		const counts = computeStructuredTagCounts(
			topLevelTags,
			next,
			distributionStatus,
		);
		if (!Object.keys(counts).length) {
			return next;
		}
		const stale = topLevelTags.filter(
			(tag) => isFacetTag(tag) && next[tag.id] && (counts[tag.id] ?? 0) === 0,
		);
		if (!stale.length) {
			return next;
		}
		next = { ...next };
		for (const tag of stale) {
			next[tag.id] = false;
		}
	}
};

export const buildDisplaySectionTags = ({
	sections,
	structuredTagCounts,
	unavailableQuestionTypeIds,
	proficiencyLabelOverride,
}: {
	sections: SectionTag[];
	structuredTagCounts: Record<number, number>;
	unavailableQuestionTypeIds: Set<number>;
	proficiencyLabelOverride?: string;
}): SectionTag[] => {
	const hasCounts = Object.keys(structuredTagCounts).length > 0;
	return sections
		.map((section) => {
			const sectionType = section.tags[0]?.type;
			if (sectionType === TAG_TYPES.proficiency && hasCounts) {
				return {
					...section,
					title: proficiencyLabelOverride ?? section.title,
					tags: section.tags.map((tag) => ({
						...tag,
						numItems: structuredTagCounts[tag.id] ?? tag.numItems,
					})),
				};
			}
			if (sectionType !== TAG_TYPES.questionType) {
				return section;
			}
			const visibleTags = unavailableQuestionTypeIds.size
				? section.tags.filter((tag) => !unavailableQuestionTypeIds.has(tag.id))
				: section.tags;
			if (!hasCounts) {
				return { ...section, tags: visibleTags };
			}
			return {
				...section,
				tags: visibleTags.map((tag) => ({
					...tag,
					numItems: structuredTagCounts[tag.id] ?? tag.numItems,
				})),
			};
		})
		.filter((section) => section.tags.length > 0);
};

export const stripLockedTagIds = (
	status: CheckboxState,
	topLevelTags: MixedItemTag[],
): CheckboxState => {
	if (!status) {
		return status;
	}

	const lockedIds = new Set<number>();
	const visit = (tag: MixedItemTag) => {
		if (tag.locked) {
			lockedIds.add(tag.id);
		}
		for (const child of tag.children ?? []) {
			visit(child);
		}
	};
	for (const tag of topLevelTags) {
		visit(tag);
	}

	const hasLockedSelection = Object.keys(status).some(
		(key) => status[key] && lockedIds.has(Number(key)),
	);
	if (!hasLockedSelection) {
		return status;
	}

	const next: NonNullable<CheckboxState> = { ...status };
	for (const id of lockedIds) {
		if (next[id]) {
			next[id] = false;
		}
	}
	return next;
};
